/**
 * SOLID Review
 * S — Strong. Step 2 of the entry funnel: the optional game-table code as typed, the check it is handed to, and the message a refusal
 *     shows. The lookup and the routing are the session's (`checkTableCode`, strict-ports FE2).
 * O — Good. A new routing case is a Domain decision; this shows its outcome unchanged.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. Type, continue, back.
 * D — Strong. Two use cases and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import type { ClientRequests } from '../client/ClientRequests';
import { SERVER_REFUSED } from '../../application/ports/contracts';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const BACK_TO_WELCOME: TClientUseCaseInput<'navigate'> = { screen: 'WELCOME' };
/** The Domain's refusal of a code no table has. */
const NOT_FOUND = 'NOT_FOUND';

export interface IStateEnterCode {
  readonly code: string;
  readonly error: string | null;
  readonly checking: boolean;
}

const EMPTY: IStateEnterCode = { code: '', error: null, checking: false };
const CHECKING: Partial<IStateEnterCode> = { error: null, checking: true };
const CHECKED: Partial<IStateEnterCode> = { checking: false };

/**
 * THE GAME-TABLE CODE (frontend-framework-oo P3, 2026-10-29, §28.295) — `components/auth/EnterCodeScreen.tsx`'s state and handlers.
 * "If you have it, enter a game table code": empty, Continue hosts a new table; a code is checked (it must exist) and kept for the
 * GAME screen to join once the player's identity is known. One check at a time.
 */
export class EnterCodeViewModel implements IServiceViewModel<IStateEnterCode> {
  private readonly state = new ObservableState<IStateEnterCode>(EMPTY);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'checkTableCode' | 'navigate'>,
    private readonly requests: ClientRequests,
  ) {}

  /** As typed: upper case, letters and digits only; a new code clears the message. */
  readonly type = (typed: string): void => {
    const code: Partial<IStateEnterCode> = { code: typed.toUpperCase().replace(/[^A-Z0-9]/g, ''), error: null };
    this.state.update(code);
  };

  readonly continue = async (): Promise<void> => {
    if (this.state.get().checking) return;
    this.state.update(CHECKING);
    try {
      const command: TClientUseCaseInput<'checkTableCode'> = { code: this.state.get().code };
      const result = await this.useCases.checkTableCode(this.requests.context(), command);
      // No such table reads as the Domain words it; the server's own refusal as the server put it.
      if (!result.success) {
        const { code: failure, message } = result.error;
        const refused: Partial<IStateEnterCode> = {
          error: failure === NOT_FOUND || failure === SERVER_REFUSED ? message : 'Could not reach the server. Please try again.',
        };
        this.state.update(refused);
      }
    } finally {
      this.state.update(CHECKED);
    }
  };

  readonly back = (): void => {
    void this.useCases.navigate(this.requests.context(), BACK_TO_WELCOME);
  };

  getSnapshot(): IStateEnterCode {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {}
}
